import React from 'react';
import { connect } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { displayDuration } from '../../../utils/parsing';
import ReactEcharts from '../../lazy/Echarts';
import { MS_TO_MINS } from '../DashboardLoader';

const BUCKET_WIDTH = 2; // minutes

const getOptions = (series) => ({
  animation: false,
  grid: {
    top: 10,
    bottom: 20,
    left: 'top',
    right: 20,
    containLabel: true,
  },
  tooltip: {
    trigger: 'axis',
    formatter: (params) => {
      const { val, unit } = displayDuration(params[0].axisValue, 'minutes');
      let tooltip = `<div>${val} ${unit}`;
      params.forEach((p) => {
        tooltip += `<br /> ${p.marker}${p.seriesName}: ${(
          p.value[1] || 0
        ).toLocaleString()} points`;
      });
      tooltip += '</div>';
      return tooltip;
    },
  },
  xAxis: {
    type: 'log',
    name: 'Data age (minutes)',
    nameLocation: 'center',
    nameGap: 25,
    min: 1,
    minInterval: 1,
    splitLine: { show: false },
  },
  yAxis: {
    type: 'log',
    name: 'Points',
    splitLine: { show: false },
  },
  color: ['#4d9de0'],
  series,
});

/**
 * A histogram of point ages by last date update, stacked per-building
 * Uses log scales for both axis to account for exponential nature of outliers
 */
const PointReadingAges = ({ series, loaded, isEmpty }) =>
  isEmpty ? null : (
    <div className="data-quality-widget">
      {!loaded ? (
        <LinearProgress color="secondary" className="loading" />
      ) : (
        <ReactEcharts notMerge style={{ height: '220px' }} option={getOptions(series)} />
      )}
    </div>
  );

export default connect((state, { buildings }) => {
  const {
    entities: {
      buildings: { byId: buildingsById },
      points: { byId: pointsById, byBuilding: pointsByBuildingId },
      ingestStats: { loaded },
    },
  } = state;
  const isEmpty =
    Object.keys(buildingsById).length === 0 || (Object.keys(pointsById).length === 0 && loaded);
  const now = new Date().getTime();
  // build up a dict-of-dicts of point count by bucketed age and building id
  /* eslint-disable no-param-reassign */
  const pointAgesByBuilding = buildings.reduce((data, bid) => {
    data[bid] =
      (pointsByBuildingId[bid] ?? [])
        .map((pid) => pointsById[pid]?.lastUpdatedTs)
        .filter((ts) => ts != null)
        .map((ts) => (now - ts) * MS_TO_MINS)
        .map((d) => Math.ceil(d / BUCKET_WIDTH) * BUCKET_WIDTH)
        .reduce((acc, bucket) => {
          acc[bucket] = (acc[bucket] || 0) + 1;
          return acc;
        }, {}) || {};
    return data;
  }, {});
  /* eslint-enable */

  const series = buildings.map((bid) => ({
    name: buildingsById[bid]?.name,
    type: 'bar',
    stack: `${buildingsById[bid]?.orgId}`, // admin view will show a stack per organization
    data: Object.entries(pointAgesByBuilding[bid]),
    barMaxWidth: '5',
    barMinHeight: '5',
  }));

  return { series, loaded, isEmpty };
})(PointReadingAges);
